/* Run math and formatting: durations, token totals, cost estimates. Pure
   functions over `Run`, read by Traces, Evals, Overview and Teams. */

import type { Run, RunTurn, TokenRates, TokenTotals, TraceStep, TurnMessage } from "@/shared/types";

export function stepEnd(s: Pick<TraceStep, "startMs" | "durMs">): number {
  return s.startMs + s.durMs;
}

/** Wall-clock milliseconds. A recorded run's is the runtime's, or so far while it runs. */
export function runDuration(run: Run, now = Date.now()): number {
  if (run.recorded) return run.recorded.duration_ms ?? Math.max(0, now - Date.parse(run.recorded.started_at));
  return Math.max(...run.turns.flatMap((t) => t.steps.map(stepEnd)));
}

export function runTokens(run: Run): TokenTotals {
  let input = 0, output = 0;
  for (const t of run.turns) for (const s of t.steps) {
    input += s.tokensIn || 0;
    output += s.tokensOut || 0;
  }
  return { input, output, total: input + output };
}

export function turnTokens(turn: RunTurn): TokenTotals {
  let input = 0, output = 0;
  for (const s of turn.steps) {
    input += s.tokensIn || 0;
    output += s.tokensOut || 0;
  }
  return { input, output, total: input + output };
}

export function turnDuration(turn: RunTurn): number {
  const start = Math.min(...turn.steps.map((s) => s.startMs));
  const end = Math.max(...turn.steps.map(stepEnd));
  return end - start;
}

// Messages carried into a turn = everything produced by earlier turns (plus the system prompt).
export function historyBefore(run: Run, turnIndex: number): TurnMessage[] {
  const msgs: TurnMessage[] = [{ role: "system", text: "Agent instructions, connected app schemas, and memory." }];
  for (const t of run.turns) {
    if (t.index >= turnIndex) break;
    msgs.push(...t.messages);
  }
  return msgs;
}

export function fmtTokens(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(1)}k`;
}

export function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}

/* --------------------------------- cost ----------------------------------- */

// Blended estimate — $3 / M input tokens, $12 / M output tokens. No model's real price.
export const TOKEN_RATES: TokenRates = { inputPerM: 3, outputPerM: 12 };

// Dollar estimate for a run, derived from its token counts. Returns a number.
export function runCost(run: Run): number {
  const t = runTokens(run);
  return (t.input / 1e6) * TOKEN_RATES.inputPerM + (t.output / 1e6) * TOKEN_RATES.outputPerM;
}

export function fmtCost(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 100) return `$${Math.round(n)}`;
  if (n >= 0.1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(3)}`;
}
